/**
 * src/utils/api.js
 * Centralized API wrapper for the MC Platform.
 * Features:
 * 1. Cookie-based session auth (httpOnly cookie sent via credentials:"include")
 * 2. CSRF protection (X-CSRF-Token attached on non-GET requests, mirrors fetchWrapper.js)
 * 3. Automatic 401 recovery (Auto-logout with redirect)
 * 4. Support for streaming (used by SEO Analyzer)
 */

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:9500/api/v1";

const getCookie = (name) => {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

export const apiFetch = async (endpoint, options = {}) => {
  const url = endpoint.startsWith("http") ? endpoint : `${API_BASE}${endpoint}`;

  // Backend requires X-CSRF-Token on every non-GET request (except a few exempt
  // auth/webhook paths) — attach it from the readable csrfToken cookie set at login.
  const method = (options.method || "GET").toUpperCase();
  const csrfToken = getCookie("csrfToken");
  const csrfHeader =
    !["GET", "HEAD", "OPTIONS"].includes(method) && csrfToken
      ? { "X-CSRF-Token": csrfToken }
      : {};

  const response = await fetch(url, {
    ...options,
    credentials: "include", // ✅ ensure cookies are sent on every request
    headers: {
      "Content-Type": "application/json",
      ...csrfHeader, // ✅ CSRF token on non-GET mutations; httpOnly cookie carries auth
      ...(options.headers || {}),
    },
  });

  // Handle Session Expiry (401)
  if (response.status === 401 && window.location.pathname !== "/login") {
    console.warn("Session expired or invalid. Redirecting to login...");
    
    // Clear local auth state
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("permissions");
    
    // Alert user before redirecting
    alert("Your session has expired. Please login again to continue.");
    window.location.href = "/login";
    return;
  }

  return response;
};
